import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  UserIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const ASSOCIATION_TYPE_KEYS: Record<string, string> = {
  sponsors: 'stakeholderManagement.roles.sponsors',
  customers: 'stakeholderManagement.roles.customers',
  employees: 'stakeholderManagement.roles.employees',
  project_team: 'stakeholderManagement.roles.project_team',
  suppliers: 'stakeholderManagement.roles.suppliers',
  regulators: 'stakeholderManagement.roles.regulators',
};

interface StakeholderAssociationItem {
  entity_type: string;
  entity_id: string;
  association_type: string;
  entity_name?: string;
}

interface Stakeholder {
  id: string;
  name: string;
  email?: string;
  title?: string;
  organization?: string;
  is_external: boolean;
  business_function?: string;
  geographic?: string;
  issues_concerns?: string;
  created_at: string;
  updated_at: string;
  associations?: StakeholderAssociationItem[];
}

interface CreateStakeholderRequest {
  name: string;
  email?: string;
  title?: string;
  organization?: string;
  is_external: boolean;
  business_function_id?: string;
  geographic_id?: string;
  issues_concerns?: string;
  user_id?: string;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  tenant_id?: string;
  tenant_name?: string;
}

export const StakeholderManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const transformationId = searchParams.get('transformation_id') || undefined;

  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState<Stakeholder | null>(null);
  const [formData, setFormData] = useState<CreateStakeholderRequest>({
    name: '',
    email: '',
    title: '',
    organization: '',
    is_external: false,
    issues_concerns: '',
    user_id: undefined
  });
  
  // User search for internal stakeholders
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const userSearchRef = React.useRef<HTMLDivElement>(null);

  // Delete confirmation dialog state
  const [stakeholderToDelete, setStakeholderToDelete] = useState<Stakeholder | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);

  useEffect(() => {
    fetchStakeholders();
  }, []);

  // Open create modal when navigated to /stakeholders/create or ?transformation_id=...
  useEffect(() => {
    if (location.pathname.endsWith('/create') || transformationId) {
      setShowCreateModal(true);
    }
  }, [location.pathname, transformationId]);

  // Search users when is_external is false and user search term changes
  useEffect(() => {
    if (!formData.is_external && userSearchTerm.length >= 2 && currentUser?.tenantId) {
      const timeoutId = setTimeout(() => {
        searchUsers(userSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setUserSearchResults([]);
    }
  }, [userSearchTerm, formData.is_external, currentUser?.tenantId]);

  // Handle click outside user search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowUserSearch(false);
      }
    };

    if (showUserSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserSearch]);

  const searchUsers = async (query: string) => {
    try {
      setUserSearchLoading(true);
      // Use the admin users endpoint to search for users
      const response = await api.get('/users/admin/users', {
        params: {
          search: query,
          limit: 20
        }
      });
      
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        // Filter to current tenant only (unless platform admin)
        const filteredUsers = users.filter((u: User) => 
          !currentUser?.tenantId || u.tenant_id === currentUser.tenantId
        );
        setUserSearchResults(filteredUsers);
      }
    } catch (error: any) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      user_id: user.id,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      email: user.email,
      organization: user.tenant_name || '', // Set organization to tenant name for internal users
      // Title is not available in users table, so leave it empty
    });
    setUserSearchTerm('');
    setShowUserSearch(false);
    setUserSearchResults([]);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setFormData({
      ...formData,
      user_id: undefined,
      name: '',
      email: '',
      organization: ''
    });
    setUserSearchTerm('');
  };

  const fetchStakeholders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stakeholders', { params: { withAssociations: 'true' } });
      setStakeholders(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching stakeholders:', error);
      setError(error.message || 'Failed to fetch stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStakeholder = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/stakeholders', formData);
      const newStakeholder = response.data.data;

      if (transformationId && newStakeholder?.id) {
        await api.post('/stakeholder-assignments', {
          stakeholder_id: newStakeholder.id,
          entity_type: 'project',
          entity_id: transformationId,
          association_type: 'project_team'
        });
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('transformation_id');
        setSearchParams(nextParams);
        if (location.pathname.endsWith('/create')) {
          navigate(`/transformations/${transformationId}`, { replace: true });
          return;
        }
      }

      await fetchStakeholders();
      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        title: '',
        organization: '',
        is_external: false,
        issues_concerns: '',
        user_id: undefined
      });
      setSelectedUser(null);
      setUserSearchTerm('');
    } catch (error: any) {
      console.error('Error creating stakeholder:', error);
      setError(error?.response?.data?.message || error.message || 'Failed to create stakeholder');
    }
  };

  const handleUpdateStakeholder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStakeholder) return;

    try {
      await api.put(`/stakeholders/${editingStakeholder.id}`, formData);
      await fetchStakeholders();
      setEditingStakeholder(null);
      setFormData({
        name: '',
        email: '',
        title: '',
        organization: '',
        is_external: false,
        issues_concerns: '',
        user_id: undefined
      });
    } catch (error: any) {
      console.error('Error updating stakeholder:', error);
      setError(error?.response?.data?.message || error.message || 'Failed to update stakeholder');
    }
  };

  const handleDeleteStakeholder = async (stakeholder: Stakeholder) => {
    try {
      await api.delete(`/stakeholders/${stakeholder.id}`);
      await fetchStakeholders();
    } catch (error: any) {
      console.error('Error deleting stakeholder:', error);
      setError(error?.response?.data?.message || error.message || 'Failed to delete stakeholder');
      throw error;
    }
  };

  const handleDeleteStakeholderWithConfirm = (stakeholder: Stakeholder) => {
    if (skipDeleteConfirmation) {
      handleDeleteStakeholder(stakeholder);
    } else {
      setStakeholderToDelete(stakeholder);
    }
  };

  const handleConfirmDeleteStakeholder = async () => {
    if (!stakeholderToDelete) return;
    await handleDeleteStakeholder(stakeholderToDelete);
  };

  const openEditModal = (stakeholder: Stakeholder) => {
    setEditingStakeholder(stakeholder);
      setFormData({
        name: stakeholder.name,
        email: stakeholder.email || '',
        title: stakeholder.title || '',
        organization: stakeholder.organization || '',
        is_external: stakeholder.is_external,
        issues_concerns: stakeholder.issues_concerns || '',
        user_id: (stakeholder as any).user_id || undefined
      });
      // If internal stakeholder with user_id, try to load user info
      if (!stakeholder.is_external && (stakeholder as any).user_id) {
        // User info will be loaded when form opens
        setSelectedUser({
          id: (stakeholder as any).user_id,
          email: stakeholder.email || '',
          first_name: stakeholder.name.split(' ')[0],
          last_name: stakeholder.name.split(' ').slice(1).join(' '),
          tenant_name: stakeholder.organization
        });
      } else {
        setSelectedUser(null);
      }
  };

  const filteredStakeholders = stakeholders.filter(stakeholder =>
    stakeholder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stakeholder.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stakeholder.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">{t('stakeholderManagement.loading') || 'Loading stakeholders...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5 text-[var(--color-accent-bg)]" />
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                  {t('stakeholderManagement.title') || 'Stakeholder Management'}
                </h1>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                {t('stakeholderManagement.addStakeholder') || 'Add Stakeholder'}
              </button>
            </div>
            <p className="text-xs text-[var(--color-secondary-text)] ml-7">
              {t('stakeholderManagement.subtitle') || 'Manage stakeholders across your organization'}
            </p>
          </div>

          <div className="mt-2">
            <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-2">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-secondary-text)] opacity-75" />
                  <input
                    type="text"
                    placeholder={t('stakeholderManagement.searchPlaceholder') || 'Search stakeholders...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-2 p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 text-[var(--color-error)] rounded-md">
              {error}
            </div>
          )}

          {/* Stakeholders List */}
          <div className="mt-2">
        <div className="grid gap-6">
          {filteredStakeholders.length === 0 ? (
            <div className="bg-[var(--color-surface-bg)] shadow rounded-lg p-8 text-center border border-[var(--color-border)]">
              <UserGroupIcon className="h-12 w-12 mx-auto text-[var(--color-secondary-text)] opacity-75 mb-4" />
              <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-2">
                {t('stakeholderManagement.noStakeholders') || 'No stakeholders found'}
              </h3>
              <p className="text-[var(--color-secondary-text)]">
                {searchTerm ? (t('stakeholderManagement.tryAdjustingSearch') || 'Try adjusting your search criteria') : (t('stakeholderManagement.getStarted') || 'Get started by adding your first stakeholder')}
              </p>
            </div>
          ) : (
            filteredStakeholders.map((stakeholder) => (
              <div key={stakeholder.id} className="bg-[var(--color-surface-bg)] shadow rounded-lg p-6 border border-[var(--color-border)]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                        {stakeholder.name}
                      </h3>
                      {stakeholder.is_external && (
                        <span className="px-2 py-1 text-xs font-medium bg-[var(--color-info)]/10 text-[var(--color-info)] rounded-full">
                          {t('stakeholderManagement.external') || 'External'}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {stakeholder.email && (
                        <div className="flex items-center space-x-2">
                          <EnvelopeIcon className="h-4 w-4 text-[var(--color-secondary-text)] opacity-75" />
                          <span className="text-sm text-[var(--color-secondary-text)]">{stakeholder.email}</span>
                        </div>
                      )}
                      
                      {stakeholder.title && (
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-[var(--color-secondary-text)] opacity-75" />
                          <span className="text-sm text-[var(--color-secondary-text)]">{stakeholder.title}</span>
                        </div>
                      )}
                      
                      {stakeholder.organization && (
                        <div className="flex items-center space-x-2">
                          <BuildingOfficeIcon className="h-4 w-4 text-[var(--color-secondary-text)] opacity-75" />
                          <span className="text-sm text-[var(--color-secondary-text)]">{stakeholder.organization}</span>
                        </div>
                      )}
                    </div>

                    {stakeholder.associations && stakeholder.associations.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-2 flex items-center gap-1">
                          <LinkIcon className="h-4 w-4" />
                          {t('stakeholderManagement.servingAs') || 'Serving as'}
                        </h4>
                        <ul className="space-y-1 text-sm text-[var(--color-secondary-text)]">
                          {stakeholder.associations.map((a, idx) => {
                            const roleLabel = ASSOCIATION_TYPE_KEYS[a.association_type] ? t(ASSOCIATION_TYPE_KEYS[a.association_type]) : (a.association_type || '').replace(/_/g, ' ');
                            const label = [a.entity_name || a.entity_type, roleLabel].filter(Boolean).join(' · ');
                            const isTransformation = /transformation|project/i.test(a.entity_type);
                            return (
                              <li key={`${a.entity_type}-${a.entity_id}-${idx}`} className="flex items-center gap-2">
                                {isTransformation && a.entity_id ? (
                                  <Link
                                    to={`/transformations/${a.entity_id}`}
                                    className="text-[var(--color-info)] hover:underline truncate"
                                  >
                                    {label || a.entity_id}
                                  </Link>
                                ) : (
                                  <span>{label || `${a.entity_type} (${a.entity_id})`}</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {stakeholder.issues_concerns && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('stakeholderManagement.issuesConcerns') || 'Issues & Concerns'}</h4>
                        <p className="text-sm text-[var(--color-secondary-text)]">{stakeholder.issues_concerns}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(stakeholder)}
                      className="p-2 text-[var(--color-secondary-text)] opacity-75 hover:text-[var(--color-info)] hover:bg-[var(--color-info)]/10 rounded-md transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteStakeholderWithConfirm(stakeholder)}
                      className="p-2 text-[var(--color-secondary-text)] opacity-75 hover:text-[var(--color-error)] hover:bg-[var(--color-error)]/10 rounded-md transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <ConfirmDialog
          isOpen={!!stakeholderToDelete}
          onClose={() => setStakeholderToDelete(null)}
          onConfirm={handleConfirmDeleteStakeholder}
          title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
          itemName={stakeholderToDelete?.name}
          description={t('messages.deleteWarning') || 'This action cannot be undone.'}
          confirmLabel={t('common.delete') || 'Delete'}
          cancelLabel={t('common.cancel') || 'Cancel'}
          variant="danger"
          showDontAskAgain={true}
          dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
          onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
        />

        {/* Create/Edit Modal */}
        {(showCreateModal || editingStakeholder) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--color-surface-bg)] rounded-lg shadow-xl border border-[var(--color-border)]">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">
                  {editingStakeholder ? (t('stakeholderManagement.editStakeholder') || 'Edit Stakeholder') : (t('stakeholderManagement.addNewStakeholder') || 'Add New Stakeholder')}
                </h2>
                
                <form onSubmit={editingStakeholder ? handleUpdateStakeholder : handleCreateStakeholder}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('stakeholderManagement.nameRequired') || 'Name *'}
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder={t('stakeholderManagement.enterName') || 'Enter stakeholder name'}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('stakeholderManagement.email') || 'Email'}
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t('stakeholderManagement.enterEmail') || 'Enter email address'}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('stakeholderManagement.titleLabel') || 'Title'}
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder={t('stakeholderManagement.enterTitle') || 'Enter professional title'}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        {t('stakeholderManagement.organization') || 'Organization'}
                      </label>
                      <input
                        type="text"
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        placeholder={t('stakeholderManagement.enterOrganization') || 'Enter organization name'}
                        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                      {t('stakeholderManagement.issuesConcerns') || 'Issues & Concerns'}
                    </label>
                    <textarea
                      value={formData.issues_concerns}
                      onChange={(e) => setFormData({ ...formData, issues_concerns: e.target.value })}
                      placeholder="Enter any issues or concerns"
                      className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_external}
                        onChange={(e) => {
                          const isExternal = e.target.checked;
                          setFormData({ 
                            ...formData, 
                            is_external: isExternal,
                            // Clear user_id and autopopulated fields when switching to external
                            ...(isExternal ? { user_id: undefined } : {})
                          });
                          if (isExternal) {
                            setSelectedUser(null);
                            setUserSearchTerm('');
                          }
                        }}
                        className="rounded border-[var(--color-border)]"
                      />
                      <span className="text-sm text-[var(--color-secondary-text)]">External stakeholder</span>
                    </label>
                  </div>

                  {/* User Search for Internal Stakeholders */}
                  {!formData.is_external && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                        Select Internal User (Optional)
                      </label>
                      {selectedUser ? (
                        <div className="flex items-center justify-between p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/20 rounded-md">
                          <div className="flex items-center space-x-2">
                            <UserIcon className="h-5 w-5 text-[var(--color-info)]" />
                            <div>
                              <div className="text-sm font-medium text-[var(--color-surface-text)]">
                                {selectedUser.first_name || selectedUser.last_name 
                                  ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim()
                                  : selectedUser.email}
                              </div>
                              <div className="text-xs text-[var(--color-secondary-text)]">{selectedUser.email}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearUser}
                            className="p-1 text-[var(--color-secondary-text)] opacity-75 hover:text-[var(--color-error)] transition-colors"
                            title="Clear user selection"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative" ref={userSearchRef}>
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-75" />
                          </div>
                          <input
                            type="text"
                            value={userSearchTerm}
                            onChange={(e) => {
                              setUserSearchTerm(e.target.value);
                              setShowUserSearch(true);
                            }}
                            onFocus={() => setShowUserSearch(true)}
                            placeholder="Search for user by name or email..."
                            className="w-full pl-10 pr-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
                          />
                          {userSearchLoading && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-info)]"></div>
                            </div>
                          )}
                          
                          {/* User Search Results Dropdown */}
                          {showUserSearch && userSearchResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {userSearchResults.map((user) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => handleUserSelect(user)}
                                  className="w-full text-left px-4 py-2 hover:bg-[var(--color-info)]/10 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <UserIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-75" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-[var(--color-surface-text)] truncate">
                                        {user.first_name || user.last_name 
                                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                          : user.email}
                                      </div>
                                      <div className="text-xs text-[var(--color-secondary-text)] truncate">{user.email}</div>
                                      {user.tenant_name && (
                                        <div className="text-xs text-[var(--color-secondary-text)] opacity-75 truncate">{user.tenant_name}</div>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {showUserSearch && userSearchTerm.length >= 2 && !userSearchLoading && userSearchResults.length === 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg p-4 text-sm text-[var(--color-secondary-text)] text-center">
                              No users found
                            </div>
                          )}
                        </div>
                      )}
                      <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
                        Search for an internal user to autopopulate name, email, and organization
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setEditingStakeholder(null);
                        setFormData({
                          name: '',
                          email: '',
                          title: '',
                          organization: '',
                          is_external: false,
                          issues_concerns: '',
                          user_id: undefined
                        });
                        setSelectedUser(null);
                        setUserSearchTerm('');
                        setShowUserSearch(false);
                      }}
                      className="px-4 py-2 text-sm font-medium text-[var(--color-secondary-text)] bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-secondary-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-[var(--color-primary-text)] bg-[var(--color-primary-bg)] border border-transparent rounded-md hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    >
                      {editingStakeholder ? 'Update Stakeholder' : 'Create Stakeholder'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};
